import { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  History,
  Eye,
  Flag,
  Frown,
  HelpCircle,
  ImagePlus,
  MessageCircle,
  MessageSquareWarning,
  Mic,
  Plus,
  Send,
  Smile,
  Star,
  StarOff,
  Volume2,
} from 'lucide-react';
import { edgeSpeechPort } from '../utils/edgeSpeechPort';
import { dialogueRequest, getDialogue, listDialogues, type Dialogue, type DialogueSummary } from '../services/osrDialogueService';
import { MathTextBlock } from './MathTextBlock';

type Recognition = {lang:string; interimResults:boolean; onresult:((e:{results:ArrayLike<ArrayLike<{transcript:string}>>})=>void)|null; onerror:((e:{error:string})=>void)|null; onend:(()=>void)|null; start:()=>void; stop:()=>void; abort:()=>void};
export function OffscreenDialogueView({subject, scope}: {subject:string;scope:string}) {
  const [dialogue,setDialogue]=useState<Dialogue|null>(null);
  const [busy,setBusy]=useState(false);
  const [failure,setFailure]=useState('');
  const [historyFailure,setHistoryFailure]=useState('');
  const [draft,setDraft]=useState('');
  const [historyOpen,setHistoryOpen]=useState(true);
  const [history,setHistory]=useState<DialogueSummary[]>([]);
  const [historyLoading,setHistoryLoading]=useState(false);
  const [picture,setPicture]=useState('');
  const [listening,setListening]=useState(false);
  const [recognitionLanguage,setRecognitionLanguage]=useState(subject==='english'?'en-US':'zh-CN');
  const [glance,setGlance]=useState(false);
  const [speech]=useState(()=>edgeSpeechPort(()=>{try{return localStorage.getItem('osr_edge_voice')||'';}catch{return '';}}));
  const [speaking,setSpeaking]=useState(false);
  const sessionId=useRef('');
  const recognition=useRef<Recognition|null>(null);
  const lock=useRef(false);
  const mounted=useRef(true);
  const retry=useRef<{path:string;body:Record<string,unknown>}|null>(null);
  const end=useRef<HTMLDivElement>(null);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;recognition.current?.abort();speech.cancel();};},[]);
  useEffect(()=>{end.current?.scrollIntoView({block:'nearest'});},[dialogue,busy]);
  function speak(text:string) {
    speech.cancel();setFailure('');setSpeaking(true);
    speech.speak(text, /[\u3400-\u9fff]/.test(text)?'zh-CN':'en-US',()=>setSpeaking(true),()=>setSpeaking(false),(error)=>{
      if(mounted.current){setSpeaking(false);setFailure(error?.message||'语音暂时无法播放，请重播');}
    });
  }
  async function refreshHistory() {
    setHistoryLoading(true);setHistoryFailure('');
    try { const items=await listDialogues(subject); if(mounted.current)setHistory(items); }
    catch(e){ if(mounted.current)setHistoryFailure(e instanceof Error?e.message:'历史对话暂不可用'); }
    finally{ if(mounted.current)setHistoryLoading(false); }
  }
  async function perform(path:string,body:Record<string,unknown>) {
    if(lock.current)return;
    lock.current=true;setBusy(true);setFailure('');
    retry.current={path,body};
    try {
      const result=await dialogueRequest(path,body);
      if(!mounted.current)return;
      setDialogue(result);retry.current=null;setDraft('');setPicture('');
      void refreshHistory();
      if(result.messages.length)speak(result.messages[result.messages.length-1].content);
    }catch(e){if(mounted.current)setFailure(e instanceof Error?e.message:'提交失败，请重试');}
    finally{lock.current=false;if(mounted.current)setBusy(false);}
  }
  useEffect(()=>{
    const key=`osr-dialogue-session:${scope}`;
    try{sessionId.current=sessionStorage.getItem(key)||crypto.randomUUID();sessionStorage.setItem(key,sessionId.current);}
    catch{sessionId.current=crypto.randomUUID();}
    void refreshHistory();
    void perform('',{subject,request_id:sessionId.current});
  },[]);
  async function openDialogue(id:string) {
    if(lock.current||listening)return;
    lock.current=true;setBusy(true);
    speech.cancel();setSpeaking(false);setFailure('');
    try {
      const result=await getDialogue(id);
      if(!mounted.current)return;
      setDialogue(result);setDraft('');setPicture('');retry.current=null;
      sessionId.current=id;
      try{sessionStorage.setItem(`osr-dialogue-session:${scope}`,id);}catch{}
    }catch(e){if(mounted.current)setFailure(e instanceof Error?e.message:'历史对话打开失败');}
    finally{lock.current=false;if(mounted.current)setBusy(false);}
  }
  function startNewDialogue() {
    if(lock.current||listening)return;
    const id=crypto.randomUUID();
    sessionId.current=id;
    try{sessionStorage.setItem(`osr-dialogue-session:${scope}`,id);}catch{}
    speech.cancel();setSpeaking(false);setDialogue(null);setDraft('');setPicture('');
    void perform('',{subject,request_id:id});
  }
  function send(action:string,feedback?:string) {
    if(!dialogue || listening)return;
    speech.cancel();setSpeaking(false);
    void perform(`/${dialogue.id}/turn`,{revision:dialogue.revision,request_id:crypto.randomUUID(),action,feedback,
      ...(action==='answer'?{message:draft,image:picture||undefined}:{})});
  }
  function record() {
    if(listening){recognition.current?.stop();return;}
    const Constructor=(window as unknown as {SpeechRecognition?:new()=>Recognition;webkitSpeechRecognition?:new()=>Recognition}).SpeechRecognition
      || (window as unknown as {webkitSpeechRecognition?:new()=>Recognition}).webkitSpeechRecognition;
    if(!Constructor){setFailure('当前浏览器不支持语音识别，请使用系统键盘语音输入、文字或图片回答。');return;}
    speech.cancel();setSpeaking(false);setFailure('');
    const instance=new Constructor();recognition.current=instance;instance.lang=recognitionLanguage;instance.interimResults=false;
    instance.onresult=e=>{
      if(!mounted.current)return;
      const transcript=Array.from(e.results).map(r=>r[0].transcript).join(' ');
      const compact=transcript.replace(/[，。！？!?\s]/g,'');
      const explicit=/^(请|帮我)?(收藏|取消收藏)(一下)?$/.test(compact)||/^(这题|这个|这张卡)?(太简单了?|太容易了?|太难了?|还可以|难度刚好|质量差)$/.test(compact);
      const confirm=!!dialogue?.pendingCommand&&/^(确认|确认了|是的|确定|取消|不确认|算了|不要|不用了)$/.test(compact);
      if(dialogue&&!draft.trim()&&!picture&&(explicit||confirm)){
        instance.stop();setListening(false);
        void perform(`/${dialogue.id}/turn`,{revision:dialogue.revision,request_id:crypto.randomUUID(),action:'answer',message:transcript});
      }else setDraft(s=>[s,transcript].filter(Boolean).join(' '));
    };
    instance.onerror=e=>{
      if(!mounted.current)return;
      const secure=window.isSecureContext||location.hostname==='localhost'||location.hostname==='127.0.0.1';
      const messages:Record<string,string>={
        'not-allowed': secure?'麦克风权限被拒绝，请在浏览器地址栏允许麦克风后重试。':'语音识别需要 HTTPS 或 localhost，请改用文字、图片，或切到安全域名。',
        'audio-capture':'没有检测到可用麦克风，请检查系统输入设备后重试。',
        'no-speech':'没有听到有效语音，可以再点一次麦克风，或直接输入文字。',
        network:'语音识别服务连接失败，请稍后重试或改用文字。',
      };
      setFailure(messages[e.error]||'语音识别暂不可用，请改用文字、图片回答。');
    };
    instance.onend=()=>{if(mounted.current)setListening(false);};
    try{instance.start();setListening(true);}catch{setFailure('麦克风启动失败，请重试');}
  }
  const shortcut=/Mac|iPhone|iPad/.test(navigator.platform)?'Command':'Ctrl';
  const button='rounded-full border border-border px-4 py-2 text-sm disabled:opacity-40';
  const iconButton='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-surface text-muted transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:opacity-40';
  const compactAction='inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-surface px-3 text-xs font-medium text-muted transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:opacity-40';
  const taskSourceLabel=dialogue?.task?.mode==='listening'?'听力原稿':dialogue?.task?.mode==='reading'?'阅读原稿':dialogue?.task?.mode==='interpreting'?'口译原稿':dialogue?.task?.source==='osr_card'?'口播卡关联知识':dialogue?.task?.source==='family_ksk'?'家庭计划知识补给':'学习任务';
  const hasAnswer=!!(draft.trim()||picture);
  const uploadImage=(file:File|undefined)=>{
    if(!file)return;
    if(file.size>500000){setFailure('请选取 500 KB 以内的图片');return;}
    const reader=new FileReader();
    reader.onload=()=>{if(mounted.current)setPicture(String(reader.result));};
    reader.readAsDataURL(file);
  };
  const feedbackActions=[
    ['favorite','收藏',Star],
    ['unfavorite','取消收藏',StarOff],
    ['too_easy','太简单',Smile],
    ['too_hard','太难',Frown],
    ['just_right','还可以',CheckCircle2],
    ['poor_quality','质量差',Flag],
  ] as const;
  const formatDate=(value:string)=>{
    const date=new Date(value);
    return Number.isNaN(date.getTime())?'':date.toLocaleString('zh-CN',{month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'});
  };
  return <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 px-4 py-5">
    <aside className={`${historyOpen?'w-64':'w-12'} mr-4 hidden min-h-0 shrink-0 flex-col border-r border-border pr-3 transition-all md:flex`} aria-label="历史对话">
      <div className="mb-3 flex items-center justify-between gap-2">
        {historyOpen?<span className="text-sm font-semibold">历史对话</span>:null}
        <button title={historyOpen?'收起历史边栏':'展开历史边栏'} aria-label={historyOpen?'收起历史边栏':'展开历史边栏'} className={iconButton} onClick={()=>setHistoryOpen(v=>!v)}>{historyOpen?<ChevronLeft size={17}/>:<History size={17}/>}</button>
      </div>
      {historyOpen?<>
        <button className="mb-3 inline-flex h-9 items-center justify-center gap-2 rounded-full border border-border bg-surface px-3 text-xs font-semibold text-primary" disabled={busy||listening} onClick={startNewDialogue}><Plus size={15}/>新对话</button>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
          {historyLoading?<p className="text-xs text-muted">正在载入历史…</p>:null}
          {history.map(item=><button key={item.id} className={`block w-full rounded-lg border p-3 text-left transition hover:border-primary/30 hover:bg-primary/5 ${dialogue?.id===item.id?'border-primary/40 bg-primary/10':'border-border bg-surface'}`} onClick={()=>openDialogue(item.id)} disabled={busy||listening}>
            <span className="block truncate text-sm font-medium">{item.title}</span>
            <span className="mt-1 block truncate text-xs text-muted">{item.lastMessage||item.summary||'暂无摘要'}</span>
            <span className="mt-2 block text-[11px] text-muted">{formatDate(item.updatedAt)}</span>
          </button>)}
          {!historyLoading&&!historyFailure&&!history.length?<p className="text-xs leading-5 text-muted">还没有历史对话。</p>:null}
        </div>
        {historyFailure?<p className="mt-2 text-xs text-red-600">{historyFailure}</p>:null}
      </>:<button title="展开历史边栏" aria-label="展开历史边栏" className={iconButton} onClick={()=>setHistoryOpen(true)}><ChevronRight size={17}/></button>}
    </aside>
    <section className="flex min-h-0 min-w-0 flex-1 flex-col">
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="rounded-2xl bg-primary/10 p-3 text-primary"><MessageCircle size={24}/></div>
        <div className="min-w-0"><h1 className="text-xl font-semibold">离屏对话</h1><p className="text-xs text-muted">从待复习的知识开始，一起把它弄明白</p></div>
      </div>
      {dialogue?.task?<div className="flex shrink-0 flex-wrap justify-end gap-1.5 text-[11px] text-muted" aria-label="当前对话统计">
        <span className="rounded-full bg-primary/10 px-2 py-1 text-primary">正在复习</span>
        <span className="rounded-full bg-canvas px-2 py-1">{taskSourceLabel}</span>
      </div>:null}
    </div>
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-4" aria-live="polite">
      {dialogue?.messages.map((message,i)=><div key={i} className={`rounded-2xl border p-4 ${message.role==='user'?'ml-8 border-primary/20 bg-primary/10':'mr-4 border-border bg-surface'}`}><p className="mb-2 text-xs text-muted">{message.role==='user'?'你':'复习伙伴'}{message.hasImage?' · 已附图片':''}</p><MathTextBlock text={message.content}/>{message.role==='assistant'?<button aria-label="重播这句话" disabled={listening} onClick={()=>speak(message.content)} className="mt-2 text-primary"><Volume2 size={16}/></button>:null}</div>)}
      {busy?<p role="status" className="text-sm text-muted">{dialogue?'正在想你刚才说的…':'正在开始对话…'}</p>:null}<div ref={end}/>
    </div>
      {dialogue?<div className="shrink-0 space-y-2 border-t border-border pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2" aria-label="快捷反馈">
          <div className="flex flex-wrap items-center gap-2">
            <button title="我不明白" aria-label="我不明白" className={compactAction} disabled={busy||listening} onClick={()=>send('clarify')}><HelpCircle size={15}/>不明白</button>
            <button title="我有质疑" aria-label="我有质疑" className={compactAction} disabled={busy||listening} onClick={()=>send('challenge')}><MessageSquareWarning size={15}/>质疑</button>
            <button title="一眼懂" aria-label="一眼懂" className={compactAction} onClick={()=>setGlance(!glance)}><Eye size={15}/>一眼懂</button>
          </div>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {dialogue.task?.mode&&dialogue.task.mode!=='question'?<button title="标记已听过" aria-label="标记已听过" className={compactAction} disabled={busy||listening} onClick={()=>send('feedback','listened')}><CheckCircle2 size={17}/>已听过</button>:null}
            {dialogue.task?.source==='osr_card'?feedbackActions.map(([kind,label,Icon])=><button key={kind} title={label} aria-label={label} className={iconButton} disabled={busy||listening} onClick={()=>send('feedback',kind)}><Icon size={17}/></button>):null}
          </div>
        </div>
        {glance?<div className="max-h-36 overflow-auto rounded-xl bg-primary/5 p-3"><p className="mb-1 text-xs text-muted">这一轮的小提示</p><MathTextBlock text={dialogue.glance || '这一轮暂时没有额外提示，可以继续问我。'}/></div>:null}
        {picture?<div className="flex items-center gap-3"><img src={picture} alt="待发送的回答" className="h-16 rounded-lg"/><button disabled={busy} onClick={()=>setPicture('')}>移除图片</button></div>:null}
        <div className="flex items-end gap-2 rounded-2xl border border-border bg-surface p-2">
          <details className="relative shrink-0">
            <summary title="添加内容" aria-label="添加内容" className={`${iconButton} cursor-pointer list-none [&::-webkit-details-marker]:hidden`}><Plus size={18}/></summary>
            <div className="absolute bottom-12 left-0 z-10 w-36 rounded-xl border border-border bg-surface p-1 shadow-lg">
              <label className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-canvas"><ImagePlus size={16}/>图片<input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" disabled={busy||listening} onChange={e=>{uploadImage(e.target.files?.[0]);e.target.value='';e.currentTarget.closest('details')?.removeAttribute('open');}}/></label>
              <select aria-label="语音识别语言" className="mt-1 w-full rounded-lg border border-border bg-surface px-2 py-2 text-sm" disabled={busy||listening} value={recognitionLanguage} onChange={e=>setRecognitionLanguage(e.target.value)}><option value="zh-CN">中文识别</option><option value="en-US">英语识别</option></select>
            </div>
          </details>
          <textarea aria-label="你的回答" value={draft} maxLength={4000} onChange={e=>setDraft(e.target.value)} disabled={busy||listening} placeholder={`说出你的回答，或写一句话…（${shortcut}+回车发送）`} onKeyDown={e=>{if(e.key==='Enter'&&(e.metaKey||e.ctrlKey)&&!e.nativeEvent.isComposing){e.preventDefault();if(!busy&&!listening&&hasAnswer)send('answer');}}} rows={1} className="min-h-10 min-w-0 flex-1 resize-none border-0 bg-transparent px-1 py-2 text-sm outline-none"/>
          {hasAnswer?<button title="发送" aria-label="发送" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary bg-primary text-white transition hover:opacity-90 disabled:opacity-40" disabled={busy||listening} onClick={()=>send('answer')}><Send size={18} className="shrink-0"/></button>:<button title={listening?'结束录音':'语音回答'} aria-label={listening?'结束录音':'语音回答'} className={iconButton} disabled={busy} onClick={record}><Mic size={18}/></button>}
        </div>
        <p className="text-xs text-muted">语音可修改后发送；“收藏一下”等明确指令会先确认。</p>
      </div>:null}

    {speaking?<button className={`${button} mt-2 self-start`} onClick={()=>{speech.cancel();setSpeaking(false);}}>停止朗读</button>:null}
    {failure?<div role="alert" className="mt-3 text-sm text-red-600">{failure}{retry.current?<button disabled={busy} className="ml-2 underline" onClick={()=>{const r=retry.current;if(r)void perform(r.path,r.body);}}>重试本轮</button>:null}</div>:null}
    </section>
  </div>;
}
